import { useEffect, useState } from 'react'
import { useSession } from 'contexts/session-context'
import SurveyService from 'services/survey.services'
import { Survey, SurveyResponse } from 'contexts/survey-context/survey.interface'
import { NEIGHBARS_SURVEY_ID } from '../constants'

export type NeighbarsSurveyStatus = 'hidden' | 'form' | 'answered'

interface UseNeighbarsSurvey {
  survey: Survey | null
  status: NeighbarsSurveyStatus
  submitAnswers: (answers: SurveyResponse[]) => Promise<boolean>
}

// Silent integration with the existing survey engine: when the campaign has
// no survey configured, the fetch fails or the API returns nothing, the form
// block must not mount at all (no loader, no error, no console output).
export const useNeighbarsSurvey = (): UseNeighbarsSurvey => {
  const [{ data: session }] = useSession()
  const [survey, setSurvey] = useState<Survey | null>(null)
  const [status, setStatus] = useState<NeighbarsSurveyStatus>('hidden')

  useEffect(() => {
    const surveyId = NEIGHBARS_SURVEY_ID
    if (surveyId === null || !session) return undefined

    let cancelled = false
    SurveyService.validateSurvey({ surveyId, isDirectAccess: false, session })
      .then((response) => {
        if (cancelled) return
        const data = response?.data as Survey | undefined
        if (!data || (!data.isAnswered && !data.questions?.length)) return
        setSurvey(data)
        setStatus(data.isAnswered ? 'answered' : 'form')
      })
      .catch((error) => {
        // The engine reports an already-answered survey as a 400 with code
        // 860; that is a valid "answered" state, not a silent failure.
        if (!cancelled && error?.response?.data?.code === 860) setStatus('answered')
      })

    return () => {
      cancelled = true
    }
  }, [session])

  const submitAnswers = async (answers: SurveyResponse[]) => {
    if (NEIGHBARS_SURVEY_ID === null || !session) return false
    try {
      await SurveyService.answerSurvey({ surveyId: NEIGHBARS_SURVEY_ID, select: answers, session })
      setStatus('answered')
      return true
    } catch {
      return false
    }
  }

  return { survey, status, submitAnswers }
}
